import { FC, useState, useEffect } from 'react';
import { Box, Button, Typography, Stack, Paper, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from 'react-router-dom';
import * as portfolioUploadApi from '../../../apis/portfolio-upload.apis';
import { useToastContext } from '../../../components/Toast/toastContext';
import { ROUTES } from '../../../contants';
import styles from '../styles';
import { PortfolioUploadTabs } from '../components/PortfolioUploadTabs';

export const PortfolioUploadViewPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { activeToast } = useToastContext();
  const [loading, setLoading] = useState(true);
  const [uploadedData, setUploadedData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await portfolioUploadApi.getPortfolioUpload(id);
        if (response.success && response.data) {
          setUploadedData(response.data);
        }
      } catch (error: any) {
        activeToast({
          type: 'error',
          text: error.msg || 'Failed to load portfolio upload',
        });
        navigate(ROUTES.PORTFOLIO_UPLOAD);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, navigate, activeToast]);

  if (loading) {
    return (
      <Box sx={styles.container}>
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '400px' }}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Stack spacing={24}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="h4">Portfolio Upload</Typography>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.PORTFOLIO_UPLOAD)}>
              Back
            </Button>
            {id && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`${ROUTES.PORTFOLIO_UPLOAD}/edit/${id}`)}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Stack>

        {uploadedData && (
          <>
            <Paper sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Name (from filename)
                </Typography>
                <Typography variant="body1">{uploadedData.name ?? '-'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  ISINs (from config sheet)
                </Typography>
                <Typography variant="body1">
                  {Array.isArray(uploadedData.isins) && uploadedData.isins.length > 0
                    ? uploadedData.isins.join(', ')
                    : uploadedData.isin ?? '-'}
                </Typography>
              </Stack>
            </Paper>

            <Box sx={styles.dataSection}>
              <PortfolioUploadTabs uploadedData={uploadedData} />
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};
